/**
 * AUDIO — named constants for every synthesized layer.
 * Gains are linear (0..1); times in seconds unless suffixed Ms.
 * Owned by AUDIO; see CONTRACTS.md.
 */

// --- Master chain ----------------------------------------------------------
export const MASTER_GAIN = 0.8
export const LIMITER_THRESHOLD_DB = -8
export const LIMITER_KNEE_DB = 2
export const LIMITER_RATIO = 20
export const LIMITER_ATTACK_S = 0.003
export const LIMITER_RELEASE_S = 0.25

// --- Shared noise -----------------------------------------------------------
export const NOISE_BUFFER_SECONDS = 2

// --- Rain bed ---------------------------------------------------------------
export const RAIN_GAIN_OUTDOOR = 0.16
export const RAIN_GAIN_INDOOR = 0.04
export const RAIN_FILTER_HZ_OUTDOOR = 1500
export const RAIN_FILTER_HZ_INDOOR = 380
export const RAIN_FILTER_Q = 0.8
/** Slow swell of the rain's lowpass cutoff. */
export const RAIN_LFO_HZ = 0.13
export const RAIN_LFO_DEPTH_HZ = 240
/** Crossfade time for indoor/outdoor transitions (contract: ~1 s). */
export const ZONE_FADE_S = 0.8
/** Fade-in when the bed first starts. */
export const BED_FADE_IN_S = 1.5

// --- Vinyl crackle ----------------------------------------------------------
export const CRACKLE_LOOP_SECONDS = 4
export const CRACKLE_GAIN = 0.045
export const CRACKLE_HIGHPASS_HZ = 2200
export const CRACKLE_MIN_GAP_S = 0.05
export const CRACKLE_MAX_GAP_S = 0.65
export const CRACKLE_IMPULSE_S = 0.004

// --- Office fan hum (indoor only) --------------------------------------------
export const FAN_HUM_FREQ_LOW_HZ = 55
export const FAN_HUM_FREQ_HIGH_HZ = 110
export const FAN_HUM_GAIN = 0.035

// --- Walking jazz bass --------------------------------------------------------
export const JAZZ_BPM = 72
export const JAZZ_BEAT_S = 60 / JAZZ_BPM
/** Swing point inside a beat where pickup eighths land. */
export const JAZZ_SWING_RATIO = 0.64
export const JAZZ_LOOKAHEAD_S = 0.15
export const JAZZ_SCHEDULER_INTERVAL_MS = 50
export const JAZZ_BASS_GAIN = 0.11
export const JAZZ_BASS_LOWPASS_HZ = 320
export const JAZZ_BASS_ATTACK_S = 0.012
/** Fraction of the beat a bass note rings before release. */
export const JAZZ_BASS_SUSTAIN_RATIO = 0.78
/** D natural-minor walking register (D2..D3), Hz. */
export const JAZZ_BASS_SCALE_HZ: readonly number[] = [
  73.42, 82.41, 87.31, 98.0, 110.0, 116.54, 130.81, 146.83
]
/** Probability an off-downbeat quarter actually sounds (sparseness). */
export const JAZZ_BEAT_PLAY_PROBABILITY = 0.55
/** Probability of a swung eighth pickup after a sounded beat. */
export const JAZZ_PICKUP_PROBABILITY = 0.16
/** Probability of a ride tick on beats 2 and 4. */
export const JAZZ_RIDE_PROBABILITY = 0.35
export const JAZZ_RIDE_GAIN = 0.022
export const JAZZ_RIDE_HIGHPASS_HZ = 6500
export const JAZZ_RIDE_DECAY_S = 0.22
export const JAZZ_BEATS_PER_BAR = 4

// --- Footsteps ----------------------------------------------------------------
export const STEP_DISTANCE_M = 0.85
/** A single player:moved jump larger than this is a teleport — no step. */
export const STEP_TELEPORT_DISTANCE_M = 3
export const STEP_GAIN = 0.14
export const STEP_DECAY_S = 0.09
export const STEP_LOWPASS_OUTDOOR_HZ = 420
export const STEP_LOWPASS_INDOOR_HZ = 700
/** Random per-step cutoff variation (multiplier range around 1). */
export const STEP_VARIATION = 0.25
export const STEP_THUMP_HZ = 62
export const STEP_THUMP_GAIN = 0.07

// --- Clue pickup (paper rustle + sting) ----------------------------------------
export const CLUE_RUSTLE_GAIN = 0.12
export const CLUE_RUSTLE_BANDPASS_HZ = 1800
export const CLUE_RUSTLE_Q = 1.2
export const CLUE_RUSTLE_S = 0.28
export const CLUE_STING_FREQ_HZ = 587.33
export const CLUE_STING_GAIN = 0.05
export const CLUE_STING_DECAY_S = 0.6

// --- Phone dial (accusation) -----------------------------------------------------
export const DIAL_CLICK_GAIN = 0.1
export const DIAL_CLICK_S = 0.012
export const DIAL_CLICK_HIGHPASS_HZ = 1200
/** Rotary pulse rate, clicks per second. */
export const DIAL_PULSE_HZ = 10
export const DIAL_DIGITS: readonly number[] = [5, 3, 7]
export const DIAL_DIGIT_GAP_S = 0.45
export const DIAL_START_DELAY_S = 0.1

// --- Resolution stingers -----------------------------------------------------------
export const STINGER_GAIN = 0.09
export const STINGER_NOTE_S = 0.9
/** Win: rising D-minor arpeggio (D3 F3 A3 D4). */
export const STINGER_WIN_FREQS_HZ: readonly number[] = [146.83, 174.61, 220.0, 293.66]
/** Lose: falling tritone pair (D2 → Ab1). */
export const STINGER_LOSE_FREQS_HZ: readonly number[] = [73.42, 51.91]
export const STINGER_INTERVAL_S = 0.22

// --- Monologue radio murmur ----------------------------------------------------------
export const MURMUR_GAIN = 0.035
export const MURMUR_BANDPASS_HZ = 850
export const MURMUR_Q = 2.5
/** Speech-rate amplitude modulation. */
export const MURMUR_AM_HZ = 5.2
export const MURMUR_FADE_S = 0.12
